import { useEffect, useState } from 'react';
import { useViewerCountry } from '../lib/geo';
import { isGdprCountry } from '../lib/gdprCountries';
import {
  getCurrentPrefs,
  isDeelScriptPresent,
  isGpcActive,
  isPlausibleScriptPresent,
  isSfGtmPresent,
  readConsent,
  saveConsent,
  syncDeel,
  syncSfGtm,
  type ConsentPrefs,
} from '../lib/consent';
import { Button } from './Button';

/**
 * GDPR/UK/Swiss opt-in consent banner.
 *
 * Visibility rule:
 *   show only when (no stored consent record) AND (viewer is in GDPR_COUNTRIES)
 *   AND (geo lookup has resolved). Non-GDPR visitors never see this banner.
 *
 * DEEL geo gate (2026-06 policy): DEEL only loads for US viewers. The
 * non-GDPR auto-load arm below is therefore narrowed to country === 'US'.
 * syncDeel() also self-gates on country, so this is belt-and-suspenders —
 * the explicit check here keeps the geo rule visible to a reader of this
 * file without having to chase into consent.ts.
 *
 * Equal-prominence rule (GDPR Art. 7 / EDPB guidance): Accept and Reject must
 * be visually equivalent — same prominence, same size, same affordance. Both
 * use the same Button variant; only their saveConsent() payload differs.
 */
export default function ConsentBanner() {
  const { country, loading } = useViewerCountry();
  const [storedConsent] = useState<ReturnType<typeof readConsent>>(() =>
    typeof window !== 'undefined' ? readConsent() : null,
  );
  const [dismissed, setDismissed] = useState(false);

  // DEEL sync for US visitors once geo resolves. Two cases:
  //
  //   - A stored record exists: sync DEEL to the RECORDED prefs. This is
  //     the first-of-session fall-through documented in index.html's DEEL
  //     block — the inline IIFE can't auto-load without a cached country,
  //     so an explicitly-consenting returning user (marketing=true) gets
  //     DEEL here, and an explicit opt-out stays unloaded.
  //   - No record: load DEEL by default (US opt-out posture). We
  //     deliberately do NOT write a synthetic consent record — that would
  //     fabricate "explicit consent" the user never gave. The opt-out path
  //     for US viewers is the marketing toggle on /privacy-settings.
  //
  // The record MUST be re-read at effect time, not taken from the
  // mount-time `storedConsent` snapshot: a choice saved on
  // /privacy-settings while /geo was still in flight has to win here —
  // an explicit local choice is never overridden (consent.ts precedence).
  //
  // For non-US non-GDPR viewers (e.g., Canada) DEEL never loads, matching
  // the geo policy. GPC gate: syncDeel and the DEEL IIFE both self-check
  // GPC as belt-and-suspenders, but bailing here keeps the intent visible
  // in this file.
  useEffect(() => {
    if (loading) return;
    if (isGpcActive()) return;
    if (isGdprCountry(country)) return;
    if (country !== 'US') return;
    const record = readConsent();
    if (record) {
      syncDeel(record.prefs);
      syncSfGtm(record.prefs);
      return;
    }
    const usDefault: ConsentPrefs = {
      necessary: true,
      analytics: false,
      marketing: true,
    };
    syncDeel(usDefault);
    syncSfGtm(usDefault);
  }, [loading, country]);

  // GPC also suppresses the banner: an active GPC signal is itself the
  // opt-out, so re-prompting the user would be both redundant and hostile.
  // initConsent has already written a deny-all record + applied runtime
  // state; the banner has nothing to add.
  const shouldShow =
    !loading &&
    !storedConsent &&
    !dismissed &&
    !isGpcActive() &&
    isGdprCountry(country);

  // Escape dismisses the banner without recording a choice — deliberately NOT
  // a reject: no consent record is written, so the banner re-shows on the
  // next visit and the GDPR default (denied) stays in force meanwhile. Same
  // keyboard pattern as the AppBar menus and the admin Modal.
  useEffect(() => {
    if (!shouldShow) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDismissed(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shouldShow]);

  if (!shouldShow) return null;

  const handleAcceptAll = () => {
    saveConsent(
      { necessary: true, analytics: true, marketing: true },
      'initial',
      'banner-accept-all',
    );
    setDismissed(true);
  };

  const handleRejectAll = () => {
    // "Reject" preserves the necessary tier (it's always true by definition)
    // and denies the two opt-in tiers.
    //
    // Reload-on-revoke: Plausible is loaded at boot for first-visit GDPR
    // viewers (cookieless, so it doesn't wait for the banner), and unloading
    // only removes the script element — the already-executed tracker keeps
    // its history hook until the page unloads. Reject must take effect NOW,
    // so if a tracker script was resident, reload after persisting the
    // record; the reloaded page stays banner-free with everything denied.
    // Capture BEFORE saveConsent — its sync path removes the elements.
    const trackerWasLive =
      isPlausibleScriptPresent() || isDeelScriptPresent() || isSfGtmPresent();
    const current = getCurrentPrefs();
    saveConsent(
      { ...current, necessary: true, analytics: false, marketing: false },
      'initial',
      'banner-reject',
    );
    if (trackerWasLive) {
      window.location.reload();
      return;
    }
    setDismissed(true);
  };

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="consent-banner-title"
      aria-describedby="consent-banner-body"
      className="fixed inset-x-0 bottom-0 z-50 flex justify-center px-4 pb-4 sm:pb-6"
    >
      <div className="w-full max-w-3xl rounded-2xl border border-white/15 bg-brand-deep/95 p-5 shadow-[0_-8px_32px_rgba(0,0,0,0.45)] backdrop-blur sm:p-6">
        <h2
          id="consent-banner-title"
          className="font-display text-base uppercase tracking-widest text-brand-redAccent sm:text-lg"
        >
          Your privacy choices
        </h2>
        <p
          id="consent-banner-body"
          className="mt-2 text-sm leading-relaxed text-white/85 sm:text-[15px]"
        >
          We use cookies and similar device storage to make this site work and
          to measure how it's used. (Advertising-measurement tags run only for
          visitors in the United States and are not loaded for you.) Choose
          how we may use these. You can change your mind at any time on the{' '}
          <a
            href="/privacy-settings"
            className="underline underline-offset-2 hover:text-brand-redAccent"
          >
            Privacy Settings
          </a>{' '}
          page. See our{' '}
          <a
            href="/privacy"
            className="underline underline-offset-2 hover:text-brand-redAccent"
          >
            Privacy Notice
          </a>{' '}
          for details.
        </p>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end sm:gap-3">
          <Button
            variant="dark"
            onClick={handleRejectAll}
            className="min-w-[180px]"
            aria-label="Reject all non-essential cookies and storage"
          >
            Reject non-essential
          </Button>
          <Button
            variant="dark"
            to="/privacy-settings"
            onClick={() => setDismissed(true)}
            className="min-w-[180px]"
            aria-label="Manage your individual cookie preferences"
          >
            Manage preferences
          </Button>
          <Button
            variant="primary"
            onClick={handleAcceptAll}
            className="min-w-[180px]"
            aria-label="Accept all cookies and storage"
          >
            Accept all
          </Button>
        </div>
      </div>
    </div>
  );
}
